import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createElementVNode as _createElementVNode, createVNode as _createVNode, vModelText as _vModelText, withDirectives as _withDirectives, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = { class: "space-y-6" }
const _hoisted_2 = { "data-members": "" }
const _hoisted_3 = { class: "flex items-center justify-between gap-2 mb-4 flex-wrap" }
const _hoisted_4 = { class: "relative flex items-center" }
const _hoisted_5 = {
  key: 0,
  class: "overflow-x-auto p-2 -m-2"
}
const _hoisted_6 = { class: "text-gray-900 font-medium truncate" }
const _hoisted_7 = { class: "min-w-0" }
const _hoisted_8 = { class: "truncate" }
const _hoisted_9 = {
  key: 0,
  class: "text-xs text-gray-500 truncate"
}

import { MagnifyingGlassIcon } from '@heroicons/vue/20/solid'
import { onBeforeUnmount, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import NeueTablist from '@/components/neue/NeueTablist.vue'
import TablistGrid from '@/components/neue/TablistGrid.vue'
import TablistHeader from '@/components/neue/TablistHeader.vue'
import TablistRow from '@/components/neue/TablistRow.vue'
import DynamicReport from '@/components/reports/DynamicReport.vue'
import { reportDetailByPath } from '@/config/reportGroups'
import { formatTimestamp } from '@/helpers/dates'
import { formatPhone } from '@/helpers/phone'
import api from '@/store/api'

const PAGE_SIZE = 25

export default {
  __name: 'TierOverviewPanel',
  props: {
  tierId: { type: String, required: true },
  tierName: { type: String, default: '' },
  version: { type: Number, default: 0 }
},
  setup(__props) {

const props = __props

const GROWTH_REPORT = reportDetailByPath['/membership-tier-growth']
const DATE_FORMAT = { month: 'short', day: 'numeric', year: 'numeric' }

const memberCols = [
  { width: 'minmax(160px, 2fr)' },
  { width: 'minmax(180px, 2fr)' },
  { width: 'minmax(110px, 1fr)' },
  { width: 'minmax(70px, 0.6fr)' },
  { width: 'minmax(110px, 1fr)' }
]

const router = useRouter()

const membersLoading = ref(true)
const members = ref([])
const page = ref(1)
const totalRecords = ref(0)
const totalPages = ref(0)
const searchQuery = ref('')
let searchDebounceTimer = null
let latestMembersFetch = 0

const formatDate = value => formatTimestamp(value, DATE_FORMAT) ?? '-'

const contactLine = member => {
  const { phone, email } = member.attributes
  if (phone) return formatPhone(phone)
  return email || '-'
}

const fetchMembers = async () => {
  const fetchId = ++latestMembersFetch
  membersLoading.value = true
  const params = new URLSearchParams({ page: page.value, page_size: PAGE_SIZE })
  if (searchQuery.value) params.set('search', searchQuery.value)
  try {
    const response = await api.call('GET', `membership_tiers/${props.tierId}/members?${params.toString()}`)
    if (fetchId !== latestMembersFetch) return
    members.value = response.data.data || []
    totalRecords.value = response.data.total_records || 0
    totalPages.value = response.data.total_pages || 0
  } catch {
    if (fetchId !== latestMembersFetch) return
    members.value = []
    totalRecords.value = 0
    totalPages.value = 0
  } finally {
    if (fetchId === latestMembersFetch) membersLoading.value = false
  }
}

const prevPage = ret => {
  if (page.value > 1) {
    page.value--
    fetchMembers()
    if (ret) ret()
  }
}

const nextPage = ret => {
  if (page.value < totalPages.value) {
    page.value++
    fetchMembers()
    if (ret) ret()
  }
}

watch(searchQuery, () => {
  clearTimeout(searchDebounceTimer)
  searchDebounceTimer = setTimeout(() => {
    page.value = 1
    fetchMembers()
  }, 300)
})

watch(
  () => props.version,
  () => {
    page.value = 1
    fetchMembers()
  }
)

watch(
  () => props.tierId,
  () => {
    clearTimeout(searchDebounceTimer)
    searchQuery.value = ''
    page.value = 1
    fetchMembers()
  },
  { immediate: true }
)

onBeforeUnmount(() => clearTimeout(searchDebounceTimer))

return (_ctx, _cache) => {
  const _component_EmptyState = _resolveComponent("EmptyState")
  const _component_NeueWell = _resolveComponent("NeueWell")
  const _component_NeuePagination = _resolveComponent("NeuePagination")
  const _component_NeueLoading = _resolveComponent("NeueLoading")

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_openBlock(), _createBlock(DynamicReport, {
      key: `${__props.tierId}:${__props.version}`,
      "report-id": _unref(GROWTH_REPORT).reportId,
      title: "Members over time",
      description: `Active ${__props.tierName} members, counted by each customer's most recent membership.`,
      "date-filter": _unref(GROWTH_REPORT).dateFilter,
      "dropdown-filter": _unref(GROWTH_REPORT).dropdownFilter,
      "dropdown-options": _unref(GROWTH_REPORT).dropdownOptions,
      "data-source": _unref(GROWTH_REPORT).dataSource,
      "membership-tier-ids": [__props.tierId]
    }, null, 8, ["report-id", "description", "date-filter", "dropdown-filter", "dropdown-options", "data-source", "membership-tier-ids"])),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _cache[1] || (_cache[1] = _createElementVNode("h3", { class: "text-base font-medium text-gray-900" }, "Members", -1)),
        _createElementVNode("div", _hoisted_4, [
          _createVNode(_unref(MagnifyingGlassIcon), { class: "pointer-events-none absolute left-3 h-4 w-4 text-gray-500" }),
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => ((searchQuery).value = $event)),
            type: "text",
            class: "h-[34px] w-56 pl-9 pr-3 rounded-full border border-gray-300 bg-white text-sm text-gray-700 placeholder-gray-500 shadow-sm focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500",
            placeholder: "Search members..."
          }, null, 512), [
            [_vModelText, searchQuery.value]
          ])
        ])
      ]),
      _createVNode(_component_NeueLoading, { loading: membersLoading.value }, {
        default: _withCtx(() => [
          (members.value.length)
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                _createVNode(NeueTablist, null, {
                  head: _withCtx(() => [
                    _createVNode(TablistHeader, null, {
                      default: _withCtx(() => [
                        _createVNode(TablistGrid, {
                          columns: memberCols,
                          header: ""
                        }, {
                          default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                            _createElementVNode("div", null, "Customer", -1),
                            _createElementVNode("div", null, "Contact", -1),
                            _createElementVNode("div", null, "Member since", -1),
                            _createElementVNode("div", null, "Orders", -1),
                            _createElementVNode("div", null, "Last order", -1)
                          ]))]),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(members.value, (member) => {
                      return (_openBlock(), _createBlock(TablistRow, {
                        key: member.id,
                        "data-member": "",
                        class: "cursor-pointer",
                        onClick: $event => (_unref(router).push(`/profile/${member.id}`))
                      }, {
                        default: _withCtx(() => [
                          _createVNode(TablistGrid, {
                            columns: memberCols,
                            class: "items-center"
                          }, {
                            default: _withCtx(() => [
                              _createElementVNode("div", _hoisted_6, _toDisplayString(member.attributes.customer_name), 1),
                              _createElementVNode("div", _hoisted_7, [
                                _createElementVNode("div", _hoisted_8, _toDisplayString(contactLine(member)), 1),
                                (member.attributes.phone && member.attributes.email)
                                  ? (_openBlock(), _createElementBlock("div", _hoisted_9, _toDisplayString(member.attributes.email), 1))
                                  : _createCommentVNode("", true)
                              ]),
                              _createElementVNode("div", null, _toDisplayString(formatDate(member.attributes.joined_at)), 1),
                              _createElementVNode("div", null, _toDisplayString(member.attributes.order_count), 1),
                              _createElementVNode("div", null, _toDisplayString(formatDate(member.attributes.last_ordered_at)), 1)
                            ]),
                            _: 2
                          }, 1024)
                        ]),
                        _: 2
                      }, 1032, ["onClick"]))
                    }), 128))
                  ]),
                  _: 1
                })
              ]))
            : _createCommentVNode("", true),
          (!membersLoading.value && !members.value.length)
            ? (_openBlock(), _createBlock(_component_NeueWell, {
                key: 1,
                class: "text-center"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_EmptyState, {
                    plural: "members",
                    description: 
              searchQuery.value
                ? 'No members match your search.'
                : `Customers appear here when they join ${__props.tierName} or are moved to it.`
            
                  }, null, 8, ["description"])
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          (totalRecords.value > PAGE_SIZE)
            ? (_openBlock(), _createBlock(_component_NeuePagination, {
                key: 2,
                size: totalRecords.value,
                from: (page.value - 1) * PAGE_SIZE + 1,
                to: Math.min(page.value * PAGE_SIZE, totalRecords.value),
                onPrev: prevPage,
                onNext: nextPage
              }, null, 8, ["size", "from", "to"]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }, 8, ["loading"])
    ])
  ]))
}
}

}